import { BRAND } from "constants/brand";
import { useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useUpdateVendorsSchedule } from "hooks/useVendors";
import { faClose, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Ring } from "@uiball/loaders";
import { Toast } from "primereact/toast";
import TimeField from "components/Ui/TimeField/TimeField";
import { WEEKDAY_OPTIONS } from "utils/dateFormat";
import "components/VendorTimes/vendorTimes.scss";

// The weekday names come from the one list in utils/dateFormat.js.
const DAYS = WEEKDAY_OPTIONS;

export default function VendorScheduleModal({ show, onClose, vendorIds }) {
    const [schedule, setSchedule] = useState({});
    const updateVendorsScheduleMutation = useUpdateVendorsSchedule();
    const toast = useRef(null);

    const toggleDayClosed = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                isClosed: !prev[day]?.isClosed,
                remove: false,
            },
        }));
    };

    const handleTimeChange = (day, field, value) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value,
                isClosed: false,
                remove: false,
            },
        }));
    };

    /** Mark a day for removal across all selected vendors */
    const removeDay = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: { remove: true },
        }));
    };

    /** Re-add a removed day with empty times */
    const addDay = (day) => {
        setSchedule((prev) => ({
            ...prev,
            [day]: { openTime: "", closeTime: "", isClosed: false, remove: false },
        }));
    };

    const submit = async () => {
        try {
            await updateVendorsScheduleMutation.mutateAsync({
                vendorIds,
                schedule,
            });
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: "خطأ",
                detail: err.message,
                life: 4000,
            });
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg">
            <Modal.Header style={{ display: "flex", justifyContent: "space-between" }}>
                <Modal.Title>تحديد أيام ومواعيد العمل</Modal.Title>
                <FontAwesomeIcon icon={faClose} onClick={onClose} size="lg" cursor="pointer" />
            </Modal.Header>
            {updateVendorsScheduleMutation.isPending ? (
                <div className="loading-spinner center">
                    <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                </div>
            ) : null}
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />
            <Modal.Body style={{ padding: "12px 16px" }}>
                {DAYS.map((day, idx) => {
                    const entry = schedule[day.key];
                    const isRemoved = entry?.remove;
                    return (
                        <div
                            key={day.key}
                            // Painted by classes, not inline, so the dark rules reach
                            // the row (see VendorTimes/vendorTimes.scss).
                            className={`vendor-times__row ${
                                idx % 2 === 0 ? "surface-inset" : "surface-card"
                            }`}
                        >
                            <strong className="vendor-times__day">{day.label}</strong>

                            <div className="vendor-times__divider" />
                            {isRemoved ? (
                                <>
                                    <span
                                        className="text-muted"
                                        style={{
                                            flex: 1,
                                            fontStyle: "italic",
                                            fontSize: "0.875rem",
                                            paddingRight: "4px",
                                        }}
                                    >
                                        سيتم إزالة الأوقات
                                    </span>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={() => addDay(day.key)}
                                        title="تعيين أوقات"
                                        style={{ flexShrink: 0 }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-1" /> تعيين
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <TimeField
                                        disabled={entry?.isClosed}
                                        value={entry?.openTime || ""}
                                        onChange={(value) =>
                                            handleTimeChange(day.key, "openTime", value)
                                        }
                                    />

                                    <span
                                        style={{
                                            flexShrink: 0,
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        إلى
                                    </span>

                                    <TimeField
                                        disabled={entry?.isClosed}
                                        value={entry?.closeTime || ""}
                                        onChange={(value) =>
                                            handleTimeChange(day.key, "closeTime", value)
                                        }
                                    />

                                    <label
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                            flexShrink: 0,
                                            marginBottom: 0,
                                            cursor: "pointer",
                                            userSelect: "none",
                                            fontSize: "0.875rem",
                                            ...(entry?.isClosed ? { color: BRAND } : {}),
                                            fontWeight: entry?.isClosed ? 600 : 400,
                                        }}
                                    >
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            style={{ marginTop: 0, cursor: "pointer" }}
                                            checked={entry?.isClosed || false}
                                            onChange={() => toggleDayClosed(day.key)}
                                        />
                                        مغلق
                                    </label>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeDay(day.key)}
                                        title="إزالة الأوقات"
                                        style={{ flexShrink: 0, marginRight: "auto" }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </>
                            )}
                        </div>
                    );
                })}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    إلغاء
                </Button>
                <Button variant="success" onClick={submit}>
                    حفظ
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
